import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { reloadWithTurbo } from "../turboHelper";
import { executeCommandWithJobCompletion, postRequestAndWaitForJobCompletion } from "../jobCompletion";
import { translate } from "../translate";
import * as TranslationsKeys from "../locale/translationsKeys";
import { applyChildElementsFilter } from "../helpers";
import { dialogConfirmationButtonClassName, dialogCancelButtonClassName } from "../dialogConstants";
import { formSubmittedEventName } from "../../Components/ViewOrEditForm/ViewOrEditFormController";
import { OnboardingElementIdentifiers } from "../_generated/OnboardingElementIdentifiers";
import { DomEvents } from "../_generated/DomEvents";

export default class DetailController extends Controller {
    static targets = [
        "filterMyPlacesInput",
        "myPlacesContainer",
        "filterAllPlacesInput",
        "allPlacesContainer",
        "contributorsPlacesContainer",
        "filterContributorsPlacesInput",
        "mapTab",
        "createNewPoi"
    ];

    static values = {
        refreshPoiVisibilityUrl: String,
        getJobUrl: String,
        importSygicPoisToDestinationUrl: String,
        inheritPoiAndReplaceItInDestinationUrl: String,
        model: String,
        isExternalUser: Boolean,
        destinationName: String,
        language: String,
        destinationUniqueId: String,
        publishUrl: String,
        publishPreviewUrl: String,
        activationCode: String,
        refreshHiddenPoisVisibilityUrl: String,
        generateTtsForWholeDestinationUrl: String,
        markTranslationAsReviewedUrl: String
    }

    readonly refreshPoiVisibilityUrlValue!: string;
    readonly getJobUrlValue!: string;
    readonly importSygicPoisToDestinationUrlValue!: string;
    readonly inheritPoiAndReplaceItInDestinationUrlValue!: string;
    readonly modelValue!: string;
    readonly isExternalUserValue!: boolean;
    readonly destinationNameValue!: string;
    readonly languageValue!: string;
    readonly destinationUniqueIdValue!: string;
    readonly publishUrlValue!: string;
    readonly publishPreviewUrlValue!: string;
    readonly activationCodeValue!: string;
    readonly refreshHiddenPoisVisibilityUrlValue!: string;
    readonly generateTtsForWholeDestinationUrlValue!: string;
    readonly markTranslationAsReviewedUrlValue!: string;

    readonly filterMyPlacesInputTarget!: HTMLInputElement;
    readonly hasFilterMyPlacesInputTarget!: boolean;
    readonly myPlacesContainerTarget!: HTMLElement;

    readonly filterAllPlacesInputTarget!: HTMLInputElement;
    readonly allPlacesContainerTarget!: HTMLElement;

    readonly filterContributorsPlacesInputTarget!: HTMLInputElement;
    readonly hasFilterContributorsPlacesInputTarget!: boolean;
    readonly contributorsPlacesContainerTarget!: HTMLElement;
    readonly mapTabTarget!: HTMLElement;

    connect() {
        if (this.hasFilterMyPlacesInputTarget) {
            applyChildElementsFilter(this.filterMyPlacesInputTarget, this.myPlacesContainerTarget, ".poi-card");
        }
        if (this.hasFilterContributorsPlacesInputTarget) {
            applyChildElementsFilter(this.filterContributorsPlacesInputTarget, this.contributorsPlacesContainerTarget, ".poi-card");
        }
        applyChildElementsFilter(this.filterAllPlacesInputTarget, this.allPlacesContainerTarget, ".poi-card");

        this.element.addEventListener(formSubmittedEventName, this.dispatchDestinationUpdatedEvent.bind(this), {});

        this.initializePlacesTabActivation();
        this.initializeMapTabActivation();
    }

    deletePoi(event) {
        const actionButton = $(event.currentTarget);
        const poiContentUniqueId = actionButton.data("poi-content-unique-id");
        const isHidden = actionButton.data("is-hidden") === "True";
        let refreshPoiVisibilityUrl = this.refreshPoiVisibilityUrlValue;
        const getJobUrl = this.getJobUrlValue;

        bootbox.confirm({
            message: isHidden ? "Really make the Place visible?" : "Really hide the Place?",
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Ok),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_Cancel),
                    className: dialogCancelButtonClassName
                }
            },
            callback(result) {
                if (!result) return;

                refreshPoiVisibilityUrl = refreshPoiVisibilityUrl
                    .replace("$isHidden", `${!isHidden}`)
                    .replace("$uniqueId", poiContentUniqueId);

                postRequestAndWaitForJobCompletion(
                    refreshPoiVisibilityUrl,
                    undefined,
                    getJobUrl,
                    actionButton,
                    () => {
                        reloadWithTurbo();
                    });
            }
        });
    }

    importSygicPoisToDestination() {
        const getJobUrl = this.getJobUrlValue;
        const importSygicPoisToDestinationUrl = this.importSygicPoisToDestinationUrlValue;
        bootbox.confirm(
            "Really import Sygic Places into the destination?",
            result => {
                if (!result) return;

                executeCommandWithJobCompletion(
                    importSygicPoisToDestinationUrl,
                    getJobUrl
                );
        });
    }

    inheritPoiAndReplaceItInDestination(event) {
        const actionButton = $(event.currentTarget);
        const getJobUrl = this.getJobUrlValue;
        let inheritPoiAndReplaceItInDestinationUrl = this.inheritPoiAndReplaceItInDestinationUrlValue;

        bootbox.confirm(`Really inherit Place and replace the Place in the destination by the inherited Place?`,
            result => {
                if (!result) return;

                const poiContentUniqueId = actionButton.data("poi-content-unique-id");
                inheritPoiAndReplaceItInDestinationUrl = inheritPoiAndReplaceItInDestinationUrl.replace("$uniqueId", poiContentUniqueId);

                postRequestAndWaitForJobCompletion(inheritPoiAndReplaceItInDestinationUrl,
                    new FormData(),
                    getJobUrl,
                    actionButton,
                    () => {
                        reloadWithTurbo();
                    });
            });
    }

    refreshProgressBar() {
        this.dispatchDestinationUpdatedEvent();
    }

    refreshHiddenPoisVisibility() {
        Turbo.visit(this.refreshHiddenPoisVisibilityUrlValue);
    }

    generateTtsForWholeDestination(event: Event) {
        const getJobUrl = this.getJobUrlValue;
        var generateTtsForWholeDestinationUrl = this.generateTtsForWholeDestinationUrlValue;
        bootbox.confirm({
            message: `<div class="mb-4">Really generate TTS for the whole destination, including destination Places, tours and tour Places?</div>
<div class="form-check">
    <input type="checkbox" id="shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPois" class="form-check-input" />
    <label class="form-check-label" for="shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPois">
        Generate TTS for important destination POIs with TTS generation disabled
    </label>
</div>
<div class="form-check">
    <input type="checkbox" id="shouldEnableTtsGenerationAndGenerateTtsForTourPois" class="form-check-input" />
    <label class="form-check-label" for="shouldEnableTtsGenerationAndGenerateTtsForTourPois">
        Generate TTS for tour POIs with TTS generation disabled
    </label>
</div>
`,
            size: "large",
            callback: result => {
                if (!result) return;

                const shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPoisCheckbox = 
                    document.getElementById("shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPois") as HTMLInputElement;
                const shouldEnableTtsGenerationAndGenerateTtsForTourPoisCheckbox = 
                    document.getElementById("shouldEnableTtsGenerationAndGenerateTtsForTourPois") as HTMLInputElement;

                generateTtsForWholeDestinationUrl = generateTtsForWholeDestinationUrl
                    .replace(
                        "$shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPois", 
                        `${shouldEnableTtsGenerationAndGenerateTtsForImportantDestinationPoisCheckbox.checked}`
                    ).replace(
                        "$shouldEnableTtsGenerationAndGenerateTtsForTourPois", 
                        `${shouldEnableTtsGenerationAndGenerateTtsForTourPoisCheckbox.checked}`
                    );

                postRequestAndWaitForJobCompletion(
                    generateTtsForWholeDestinationUrl,
                    undefined,
                    getJobUrl,
                    $(event.target as HTMLElement)
                );
            }
        });
    }

    activateMapTab() {
        // @ts-ignore
        $(this.mapTabTarget).tab('show');
    }

    dispatchDestinationUpdatedEvent() {
        this.dispatch(DomEvents.destinationUpdated);
    }

    initializePlacesTabActivation() {
        $(`#${OnboardingElementIdentifiers.destinationsDetailPlacesTabId}`).on("shown.bs.tab", () => {
            this.dispatch(DomEvents.destinationDetailPlacesTabShown);
        });
    }

    initializeMapTabActivation() {
        $(`#${OnboardingElementIdentifiers.destinationsDetailMapTabId}`).on("shown.bs.tab", () => {
            this.dispatch(DomEvents.destinationDetailMapTabShown);
        });
    }

    markTranslationAsReviewed(event) {
        const markTranslationAsReviewedUrl = this.markTranslationAsReviewedUrlValue;
        const getJobUrl = this.getJobUrlValue;
        const actionButton = $(event.currentTarget);

        bootbox.confirm({
            message: "Really confirm the destination Overview tab translation has been reviewed?",
            buttons: {
                confirm: {
                    label: translate(TranslationsKeys.Global_Ok),
                    className: dialogConfirmationButtonClassName
                },
                cancel: {
                    label: translate(TranslationsKeys.Global_Cancel),
                    className: dialogCancelButtonClassName
                }
            },
            callback(result) {
                if (!result) return;

                postRequestAndWaitForJobCompletion(
                    markTranslationAsReviewedUrl,
                    undefined,
                    getJobUrl,
                    actionButton,
                    () => {
                        reloadWithTurbo();
                    });
            }
        });
    }
}